import React, { memo } from 'react';
import { styled } from '@compiled/react';
import Avatar, { AVATAR_SIZES } from '@atlaskit/avatar';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { ExperienceSuccess, ExperienceFailed } from '@atlassian/jira-business-experience-tracking';
import { ThemedLink } from '@atlassian/jira-business-theme-components';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src';
import { Tokens } from '@atlassian/jira-custom-theme-constants';
import { useIntl } from '@atlassian/jira-intl';
import { OVERVIEW_DETAILS_EXPERIENCE } from '../../../../common/constants';
import { useSummaryFeatures } from '../../../../controllers/features-context';
import type { useOverviewMeta } from '../../../../controllers/overview-meta';
import messages from './messages';

type Props = Pick<ReturnType<typeof useOverviewMeta>, 'data' | 'error' | 'loading'>;

const Content = ({ data, error, loading }: Props) => {
	const { formatMessage } = useIntl();
	const { linksTarget } = useSummaryFeatures();

	if (error || data == null) {
		return (
			<ErrorPill>
				<ErrorIcon label="" primaryColor={token('color.icon.danger', R400)} />
				<ErrorMessage>{formatMessage(messages.errorMessage)}</ErrorMessage>
				{error && <ExperienceFailed experience={OVERVIEW_DETAILS_EXPERIENCE} error={error} />}
			</ErrorPill>
		);
	}

	return (
		<Wrapper>
			<ScrollableWrapper>
				<table>
					<TableHead>
						<tr>
							<TableHeader>{formatMessage(messages.projectName)}</TableHeader>
							<TableHeader>{formatMessage(messages.projectKey)}</TableHeader>
							<TableHeader>{formatMessage(messages.projectLead)}</TableHeader>
						</tr>
					</TableHead>
					<TableBody>
						{data?.map((project) => (
							<tr key={project.id}>
								{loading ? (
									<>
										<TableData>
											<FlexCell>
												<ProjectAvatarSkeleton data-testid="business-summary.ui.summary-view.overview-details.content.project-avatar-skeleton" />
												<NameSkeleton />
											</FlexCell>
										</TableData>
										<TableData>
											<ProjectKeySkeleton />
										</TableData>
										<TableData>
											<FlexCell>
												<AvatarSkeleton />
												<NameSkeleton />
											</FlexCell>
										</TableData>
									</>
								) : (
									<>
										<TableData>
											<FlexCell>
												<ProjectAvatar
													src={project.avatarUrl}
													alt=""
													data-testid="business-summary.ui.summary-view.overview-details.content.project-avatar"
												/>
												<ThemedLink
													href={`/jira/core/projects/${project.key}/summary`}
													target={linksTarget}
													data-testid="business-summary.ui.summary-view.overview-details.content.project-name"
												>
													{project.name}
												</ThemedLink>
											</FlexCell>
										</TableData>
										<TableData data-testid="business-summary.ui.summary-view.overview-details.content.project-key">
											{project.key}
										</TableData>
										<TableData>
											<FlexCell>
												<Avatar size="small" src={project.lead?.picture} />
												<ThemedLink
													href={`/secure/ViewProfile.jspa?accountId=${project.lead?.accountId}`}
													target={linksTarget}
													data-testid="business-summary.ui.summary-view.overview-details.content.project-lead"
												>
													{project.lead?.name}
												</ThemedLink>
											</FlexCell>
										</TableData>
									</>
								)}
							</tr>
						))}
					</TableBody>
				</table>
			</ScrollableWrapper>
			{!loading && <ExperienceSuccess experience={OVERVIEW_DETAILS_EXPERIENCE} />}
		</Wrapper>
	);
};

export default memo(Content);

// This represents a height large enough to display 4.5 rows.
// We display .5 of a row to give the user a hint that
// they should scroll to view the remaining projects.
const MAX_HEIGHT = 190;
const PILL_HEIGHT = 64;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	backgroundColor: token('color.background.neutral', '#091E420F'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 3}px`,
	padding: `${token('space.300', '24px')} ${token('space.300', '24px')} ${token(
		'space.200',
		'16px',
	)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScrollableWrapper = styled.div({
	margin: `0 ${token('space.negative.200', '-16px')}`,
	maxHeight: `${MAX_HEIGHT}px`,
	overflowY: 'auto',
	padding: `0 ${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHead = styled.thead({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT_SUBTLE,
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${Tokens.COLOR_BORDER}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeader = styled.th({
	fontWeight: 'normal',
	padding: `0 ${token('space.200', '16px')} ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableBody = styled.tbody({
	border: 'none',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'tr:first-of-type td': {
		paddingTop: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableData = styled.td({
	padding: `${token('space.050', '4px')} ${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexCell = styled.div({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.100', '8px'),
	maxWidth: '300px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Skeleton = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: Tokens.COLOR_SKELETON,

	borderRadius: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2.5}px`,
	width: '100px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectKeySkeleton = styled(Skeleton)({
	width: '30px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarSkeleton = styled(Skeleton)({
	borderRadius: '50%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES.small}px`,
	margin: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES.small}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorPill = styled.div({
	alignItems: 'center',
	backgroundColor: token('color.background.neutral', '#091E420F'),

	borderRadius: `${PILL_HEIGHT / 2}px`,
	boxSizing: 'border-box',
	display: 'flex',
	height: `${PILL_HEIGHT}px`,
	justifyContent: 'center',
	padding: `0 ${token('space.500', '40px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessage = styled.span({
	marginLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectAvatar = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3.5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectAvatarSkeleton = styled(Skeleton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3.5}px`,
});

const NameSkeleton = Skeleton;
